import _ from "underscore";
import dayjs from "dayjs";

import { copyRichTextFiles, handleFileTransform } from "./FileUtils";
import TenantAPI from "../api/api/TenantAPI";
import FieldUtils from "./FieldUtils";
import Constants from "./Constants";
import StrUtils from "./StrUtils";

export default class InstanceUtils {
  static async formatRecordCreationPayload(
    fields,
    payload,
    isDuplicate = false
  ) {
    if (!fields || !payload) {
      return false;
    }

    if (Array.isArray(payload)) {
      let out = [];
      let payloadKeys = [];
      for (let j = 0; j < payload.length; ++j) {
        const payloadItem = payload[j];
        payloadKeys = Object.keys(payloadItem);

        for (let i = 0; i < payloadKeys.length; i++) {
          let field;

          if (
            ["_ModuleAPIName", "_SubitemParentAPIName"].includes(payloadKeys[i])
          ) {
            field = { APIName: payloadKeys[i], FieldType: "Text" };
          } else {
            field = _.findWhere(fields, { APIName: payloadKeys[i] });
          }

          if (!field) {
            continue;
          }

          if (
            field.FieldType === Constants.FIELDTYPES.SINGLE_FILE &&
            payloadItem[field.APIName]
          ) {
            // TODO: upload file here
            let data = await TenantAPI.uploadFile({
              file: payloadItem[field.APIName],
              sources: undefined,
            });

            out[i][field.APIName] = data.data.data.paths[0];
          } else {
            const objectKey = this.formatInputComponentResponse(
              field,
              payloadItem[field.APIName]
            );
            out[j] = { ...out[j], [field.APIName]: objectKey };
          }
        }
        return out;
      }
    } else {
      let out = {};
      let payloadKeys = [];
      payloadKeys = _.keys(payload);
      for (let i = 0; i < payloadKeys.length; i++) {
        let field;

        if (["_ModuleAPIName"].includes(payloadKeys[i])) {
          field = { APIName: payloadKeys[i], FieldType: "Text" };
        } else {
          field = _.findWhere(fields, { APIName: payloadKeys[i] });
        }

        if (!field) {
          continue;
        }

        if (
          field.FieldType === Constants.FIELDTYPES.SINGLE_FILE &&
          payload[field.APIName] &&
          isDuplicate
        ) {
          if (payload[field.APIName]?.length > 0) {
            const newFiles = await Promise.all(
              payload[field.APIName].map(async (file) => {
                file.Name = `Copy of ${file.Name}`;
                file.id = StrUtils.uuidv4();
                return await handleFileTransform(file, field.Public);
              })
            );
            out[field.APIName] = [...newFiles];
          }
        } else if (
          [
            Constants.FIELDTYPES.IMAGE,
            Constants.FIELDTYPES.AUDIO,
            Constants.FIELDTYPES.VIDEO,
          ].includes(field.FieldType) &&
          payload[field.APIName] &&
          isDuplicate
        ) {
          const newFile = await handleFileTransform(
            payload[field.APIName],
            field.Public
          );
          out[field.APIName] = {
            ...newFile,
            id: StrUtils.uuidv4(),
            Name: `Copy of ${newFile.Name}`,
          };
        } else if (
          field.FieldType === Constants.FIELDTYPES.SUBITEM &&
          payload[field.APIName] &&
          isDuplicate
        ) {
          out[field.APIName] = await this.formatSubitemDuplicationPayload(
            field.SubitemFields,
            payload[field.APIName]
          );
        } else if (
          field.FieldType === Constants.FIELDTYPES.MULTI_SUBITEM &&
          payload[field.APIName] &&
          isDuplicate
        ) {
          const newSubitems = await Promise.all(
            payload[field.APIName].map(async (subitem) => {
              return await this.formatSubitemDuplicationPayload(
                field.SubitemFields,
                subitem
              );
            })
          );

          out[field.APIName] = [...newSubitems];
        } else if (
          field.FieldType === Constants.FIELDTYPES.RICH_TEXT &&
          payload[field.APIName] &&
          isDuplicate
        ) {
          const newRichText = await copyRichTextFiles(payload[field.APIName]);

          out[field.APIName] = this.formatInputComponentResponse(
            field,
            newRichText
          );
        } else {
          out[field.APIName] = this.formatInputComponentResponse(
            field,
            payload[field.APIName]
          );
        }
      }

      return out;
    }
  }

  /**
   *
   * @param {Array} fields
   * @param {any} payload
   */
  static async formatSubitemDuplicationPayload(fields, payload) {
    const out = {};
    if (!fields || !payload) {
      return false;
    }

    for (let i = 0; i < fields.length; i++) {
      const field = fields[i];
      if (
        field.FieldType === Constants.FIELDTYPES.SINGLE_FILE &&
        payload[field.APIName]
      ) {
        const newFiles = await Promise.all(
          payload[field.APIName].map(async (file) => {
            file.Name = `Copy of ${file.Name}`;
            file.id = StrUtils.uuidv4();
            return await handleFileTransform(file, field?.Public);
          })
        );
        out[field.APIName] = [...newFiles];
      } else if (
        [
          Constants.FIELDTYPES.AUDIO,
          Constants.FIELDTYPES.VIDEO,
          Constants.FIELDTYPES.IMAGE,
        ].includes(field.FieldType) &&
        payload[field.APIName]
      ) {
        const newFile = await handleFileTransform(
          payload[field.APIName],
          field?.Public
        );
        out[field.APIName] = {
          ...newFile,
          id: StrUtils.uuidv4(),
          Name: `Copy of ${newFile.Name}`,
        };
      } else if (
        payload[field.APIName] &&
        field.FieldType !== Constants.FIELDTYPES.TAGS
      ) {
        out[field.APIName] = payload[field.APIName];
      }
    }

    out._ModuleAPIName = payload._ModuleAPIName;
    out._RecordName = payload._RecordName;
    out._SubitemParentAPIName = payload._SubitemParentAPIName;
    out.id = StrUtils.uuidv4();

    return out;
  }

  static formatInputComponentResponse(field, value) {
    if (!value) return value;
    const type = field.FieldType;

    // If DateTime value is already a string, we don't want to convert it again.
    if (
      typeof value === "string" &&
      (type === Constants.FIELDTYPES.DATE_TIME ||
        type === Constants.FIELDTYPES.DATE ||
        type === Constants.FIELDTYPES.TIME)
    ) {
      return value;
    }

    if (type === Constants.FIELDTYPES.DATE_TIME) {
      return value.toISOString();
    } else if (type === Constants.FIELDTYPES.DATE) {
      return value.toISOString();
    } else if (type === Constants.FIELDTYPES.TIME) {
      let v2 = value;

      if (value instanceof Date) {
        v2 = dayjs(value.toISOString());
      }

      if (v2) {
        return v2.format("HH:mmZZ");
      }

      return value;
    } else if (type === Constants.FIELDTYPES.RICH_TEXT) {
      const richText =
        typeof value === "string" ? value : value?.RichText ?? "";
      const plainText = StrUtils.convertMarkupToPlaintext(richText);

      return {
        RichText: richText,
        PlainText: plainText,
      };
    } else if (type === Constants.FIELDTYPES.SINGLE_REFERENCE) {
      if (typeof value === "string") return value;

      return {
        id: value.id,
        _id: value._id,
        _ModuleAPIName: value._ModuleAPIName,
        _RecordName: value._RecordName,
        Name: value?.Name,
      };
    } else if (type === Constants.FIELDTYPES.MULTI_REFERENCE) {
      if (!Array.isArray(value) || typeof value?.[0] === "string") return value;

      return value.map((v) => ({
        id: v.id,
        _id: v._id,
        _ModuleAPIName: v._ModuleAPIName,
        _RecordName: v._RecordName,
        Name: v?.Name,
      }));
    }

    return value;
  }

  static getDefaultValueFunctionOptions() {
    return {
      CURRENT_TIMESTAMP: () => {
        const now = new Date();

        return now.toISOString();
      },
      CURRENT_TIME: () => {
        const now = new Date();

        return now.toISOString();
      },
    };
  }

  // Verify that required fields in a Query are not blank.
  static verifyQueryObject(query) {
    const queryFilters = query.Filters;

    for (const filterGroup of queryFilters) {
      for (const filter of filterGroup.Filters) {
        const valueVerified = filter?.Value && filter.Value !== "";
        const minMaxVerified =
          filter?.Min && filter.Min !== "" && filter.Max !== "";

        const valuePasses = valueVerified || minMaxVerified;

        if (!filter.Field || !filter.Method || !valuePasses) {
          return "Please fill out all opened fields.";
        }
      }
    }

    return "Passed";
  }

  static removeDefaultSubitemFields(module) {
    const newModule = JSON.parse(JSON.stringify(module));

    newModule.Fields = newModule?.Fields?.flatMap((f) => {
      if (FieldUtils.fieldTypeIsSubitem(f?.FieldType)) {
        f.SubitemFields =
          f?.SubitemFields?.filter((f) => !f?.SystemField) ?? [];

        return [f];
      }

      return [f];
    });

    return newModule;
  }

  // Remove the default fields from a module
  static removeDefaultModuleFields(module, filterTags) {
    if (!module?.Fields) return module;

    const newModule = JSON.parse(JSON.stringify(module));

    newModule.Fields = newModule?.Fields?.flatMap((f) => {
      if (!filterTags && f?.FieldType === "Tags") return [f];

      if (f?.SystemField || f?.APIName === "_id") return [];

      if (FieldUtils.fieldTypeIsSubitem(f?.FieldType)) {
        f.SubitemFields =
          f?.SubitemFields?.filter((f) => !f?.SystemField) ?? [];

        return [f];
      }

      return [f];
    });

    return newModule;
  }
}
